import {
  BLACK_HEX,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_SETTING
} from "constants/chart.constants";
import { EvaSource, TimeStep } from "constants/charts.enums";
import { createMergeWithDefaults } from "entities/utils";
import { CrossPlotChartRequestType } from "types";

import { MaxBinsSortOrderEnum } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { RegressionType } from "models/regressionType";

import { PdenSourceEnum } from "../../../../models/pdenDataSourceSetting";
import { BASE_CHART_REQUEST } from "./defaults";

export const DEFAULT_CROSS_PLOT_REQUEST: Partial<CrossPlotChartRequestType> = {
  ...BASE_CHART_REQUEST,
  FilterId: "",
  requestId: "",
  ReverseColor: false,
  ColorPalette: {
    id: "-6",
    name: "Portland",
    shared: false,
    colors: ["rgb(12,51,131)"],
    preferredColors: [],
    paletteType: 0,
    colorScale: [],
    specialColors: null,
    thickness: [2],
    reverse: false
  },
  GroupBy: {
    title: "Resource Play",
    property: "Header.ResourcePlay",
    groupByField: "Header.ResourcePlay",
    pdenSource: "Public",
    canBin: false,
    dataType: "Text",
    categoryId: 1,
    tooltip: "",
    display: "",
    entityKind: EntityKind.Well,
    bin: {
      BinSize: 0,
      MinSize: null,
      MaxBins: null,
      MaxBinsSortOrder: MaxBinsSortOrderEnum.WellCount,
      GreaterThan: null,
      LessThan: null,
      Quantile: {
        quantileType: "NumberOfQuantile",
        numQuantiles: 4
      },
      BinType: "BinSize",
      UseDynamicBins: false
    },
    mode: undefined,
    // Declared so createMergeWithDefaults keeps it: getAllowedKeys builds its allow-list from
    // this default object, and any key absent here is silently dropped from the request. Without
    // it a chart on Forecast Trumping sent a trumping-qualified property with no settings, and
    // the backend only runs the per-well source fallthrough when the settings are present.
    forecastTrumpingSettings: undefined,
    performanceDateRange: undefined
  },
  sortBy: "AlphabeticalAsc",
  typeWells: [],
  normalizeTypeWell: true,
  userForecast: [],
  userForecastSettings: {
    forecastSourceFolder: null,
    excludeMcDanielForecasts: false,
    reserveCategory: null,
    excludeNoviForecasts: true,
    specialForecastFolders: ["McDaniel Research"]
  },
  forecastMode: "forecastTrump",
  useForecastStartDate: false,
  showGroupsNotInFilter: true,
  placeholderBinsEnabled: false,
  hasCheckedBins: true,
  syncClientPden: false,
  syncClientSchema: "",
  colorLockedItems: {},
  syncClientFcst: false,
  syncForecastRescat: "2111",
  normalizeBySetting: DEFAULT_NORMALIZE_BY_SETTING,
  chartSetting: {
    product: "Oil",
    chartType: 0,
    showForecast: false,
    pdenDataSourceSetting: {
      source: EvaSource.Public,
      timeStep: TimeStep.Month
    },
    forecastMode: "forecastTrump",
    useForecastStartDate: false,
    survivorBias: false,
    rateType: 0,
    cutoff: 0,
    timeStep: TimeStep.Month,
    shutInMonths: 0,
    source: EvaSource.Public,
    normalizeBy: DEFAULT_NORMALIZE_BY,
    useWeightedRatioAverage: false,
    xVariable: {
      valueProperty: "Dates.Vintage_YearMonth",
      displayTitle: "Vintage (Year-Month)",
      canBin: false,
      dataType: "Text",
      mappingName: "Dates.Vintage_YearMonth",
      bin: null,
      pdenSource: PdenSourceEnum.Public.toString()
    },
    yVariable: {
      valueProperty: "Performance.Oil.TwelveMCalTimeCum",
      displayTitle: "12M CalTime Cum (Mbbl)",
      canBin: true,
      dataType: "Number",
      mappingName: "Performance.Oil.TwelveMCalTimeCum",
      pdenSource: PdenSourceEnum.Public.toString()
    },
    lockUnits: false
  },
  axisMinMax: {
    xMin: null,
    xMax: null,
    yMin: null,
    yMax: null
  },
  isDefaultLockOn: true,
  chartFieldInformation: {
    xField: {
      type: "x",
      title: "Vintage (Year-Month)",
      property: "Dates.Vintage_YearMonth",
      pdenSource: PdenSourceEnum.Public,
      folderName: "",
      isForecast: false,
      product: "",
      defaultUnit: "Year-Month",
      performanceDateRange: undefined
    },
    yField: {
      type: "y",
      product: "Oil",
      defaultUnit: "Mbbl",
      title: "12M CalTime Cum + Forecast (Mbbl)",
      property: "Performance.Oil.TwelveMCalTimeCum.fcst",
      pdenSource: PdenSourceEnum.Public,
      folderName: "",
      isForecast: true,
      performanceDateRange: undefined
    },
    groupByField: {
      type: "groupBy",
      title: "Resource Play",
      property: "Header.ResourcePlay",
      defaultUnit: "",
      product: "",
      isForecast: false,
      folderName: "",
      performanceDateRange: undefined
    }
  },
  showCrossPlotLineOfBestFit: false,
  lineOfBestFitInformation: {
    regressionType: RegressionType.Linear,
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS,
    forceRegressionThroughOrigin: false
  }
};

export const generateCrossPlotRequestBody = createMergeWithDefaults(
  DEFAULT_CROSS_PLOT_REQUEST
);
